import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import * as Constants from '@emi/shared/constants';
import { Category, Sku, UnitMeasure } from '@emi/shared/models';
import { SkuSearchState, getNextPageSkuSearchResults, selectSkuSearchState } from '@emi/shared/state/sku-search';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../../../apps/via-shell/src/environments/environment';
import { SkuMetadataService } from '@emi/shared/data-access';

@Component({
    selector: 'via-results-list',
    templateUrl: './results-list.component.html',
    styleUrls: ['./results-list.component.scss'],
})
export class ResultsListComponent implements OnInit, OnDestroy {
    public noImagePlaceholderUrl = Constants.GlobalVariables.PLACEHOLDER_45x65;
    public displayedColumns: string[] = ['skuDetails', 'totalSkuSize', 'lastSeenPrice'];
    public dataSource = new MatTableDataSource<Sku>([]);
    public pageIds: string[] = [];
    public isLoading: boolean = false;
    public includeSearchPhrase: boolean | undefined;
    public totalSkuSizeTooltip = Constants.Tooltips.TOTAL_SKU_SIZE;
    public blobStorageUrl: string = environment.imagesUrl + Constants.GlobalVariables.SKU_IMAGES_SMALL;
    public imageSasParams: string;

    private readonly firstChildFromArray: number = 0;
    private readonly categoryLevelOne: number = 1;
    private readonly measureNameCountPerUnit: string = 'Count per Unit';
    private readonly measureNameQuantity: string = 'Quantity';
    private readonly measureNameVolume: string = 'Volume';
    private readonly measureNameWeight: string = 'Weight';
    private readonly lengthZero: number = 0;

    private resultsSubscription: Subscription;

    constructor(private router: Router, private store: Store<SkuSearchState>, private skuMetadataService: SkuMetadataService) { }

    public ngOnInit(): void {
        this.skuMetadataService.getImageSas().subscribe((sas) => {
            if (sas) {
                this.imageSasParams = '?' + sas;
            } else {
                this.imageSasParams = '';
            }
        });

        this.resultsSubscription = this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.dataSource.data = state.skuResults;
            this.pageIds = state.pageIds;
            this.isLoading = state.isLoading;
            this.includeSearchPhrase = state.includeSearchPhrase;
        });

    }

    public onScrollDown(): void {
        if (this.pageIds && this.pageIds[this.pageIds.length - 1] !== '') {
            this.store.dispatch(getNextPageSkuSearchResults());
        }
    }

    public handleMissingImage(id: number) {
        document.getElementById(id.toString())?.setAttribute('src', this.noImagePlaceholderUrl);
    }

    public getSkuCapsule(sku: Sku): void {
        this.router.navigate([Constants.Routes.SKU_SEARCH_SKU], { queryParams: { id: sku.emiItemId } });
    }

    public getLevelOneCategoryDetails(element: Sku): Category | null {
        let category: Category | null;
        if (element) {
            const categoryDetails = element.category?.childCategories?.filter((item) => item?.level === this.categoryLevelOne)?.[
                this.firstChildFromArray
            ];
            category = categoryDetails ?? null;
        } else {
            category = null;
        }
        return category;
    }

    public getSubCategory(sku: Sku): Category | null {
        // Skips level 0 (industry) and level 1 (top category) and finds terminal node category.
        const childCategories = sku.category.childCategories;
        if (childCategories && childCategories[0].childCategories) {
            return this.getLowestLevelCategory(childCategories[0].childCategories[0]);
        }
        return null;
    }

    private getLowestLevelCategory(category: Category): Category {
        if (!category.childCategories) {
            return category;
        }
        return this.getLowestLevelCategory(category.childCategories[0]);
    }

    public getPackSize(element: Sku): UnitMeasure | null {
        let unitMeasure: UnitMeasure | null;
        if (element?.measures?.length) {
            const packSize = element.measures.filter((x) => x.measureName === this.measureNameCountPerUnit)?.[0];
            unitMeasure = packSize ?? null;
        } else {
            unitMeasure = null;
        }
        return unitMeasure;
    }

    public getUnitPerPack(element: Sku): UnitMeasure | null {
        let unitMeasure: UnitMeasure | null;
        if (element?.measures?.length) {
            const unitPerPack = element.measures.filter((x) => x.measureName === this.measureNameQuantity)?.[0];
            unitMeasure = unitPerPack ?? null;
        } else {
            unitMeasure = null;
        }
        return unitMeasure;
    }

    public getUnitSize(element: Sku): UnitMeasure[] | null {
        let unitMeasure: UnitMeasure[] | null;
        if (element?.measures?.length) {
            const unitSize = element.measures.filter(
                (x) => x.measureName != this.measureNameQuantity && x.measureName != this.measureNameCountPerUnit
            );
            unitMeasure = unitSize && unitSize.length ? unitSize : null;
        } else {
            unitMeasure = null;
        }
        return unitMeasure;
    }

    public getUnitSizeText(element: Sku): string | null {
        const unitMeasure = this.getUnitSize(element);
        let unitSizeText: string | null = '';
        if (unitMeasure?.length) {
            unitMeasure.forEach((item) => {
                const unitName = item && item.totalMeasure && item.unitName ? item.totalMeasure + item.unitName : '';
                if (unitName !== '') {
                    unitSizeText += unitName + ', ';
                }
            });
            unitSizeText = unitSizeText ? unitSizeText.trim().substring(this.lengthZero, unitSizeText.lastIndexOf(',')) : null;
        } else {
            unitSizeText = null;
        }
        return unitSizeText;
    }

    public getTotalSKUSize(element: Sku): string | number | null {
        let totalSKUSize: string | number | null = null;
        if (element?.measures?.length) {
            const defaultMeasure = element.measures.filter((x) => x.isDefault && x.isDefault === 1)?.[0];
            if (defaultMeasure && defaultMeasure.measureName) {
                switch (defaultMeasure.measureName) {
                    case this.measureNameQuantity:
                        totalSKUSize = element.totalSkuSize;
                        break;
                    case this.measureNameVolume:
                    case this.measureNameWeight:
                        totalSKUSize = element.totalSkuSize + defaultMeasure.unitName;
                        break;
                    default:
                        break;
                }
            }
        } else {
            totalSKUSize = element.totalSkuSize;
        }
        return totalSKUSize;
    }

    //TODO: Will be brought in at a later stage

    // public selection = new SelectionModel<TableRow>(true, []);

    // public feedbackTooltipText =
    //     'Indicates if feedback given on the SKU is currently being reviewed. A clear flag indicates no feedback or review has been completed, while an orange flag indicates feedback has been submitted or a review is in progress.';
    // public removeSkuTooltipText = 'Remove this SKU';
    // public searchParams$!: SearchParams;

    // public deleteItemFromList(id: number): void {
    //     this.MOCK_DATA.filter((x) => x.id !== id);
    // }

    // public isAllSelected() {
    //     const numSelected = this.selection.selected.length;
    //     const numRows = this.dataSource.data.length;
    //     return numSelected === numRows;
    // }

    // public masterToggle() {
    //     if (this.isAllSelected()) {
    //         this.selection.clear();
    //         return;
    //     }

    //     this.selection.select(...this.dataSource.data);
    // }

    // public checkboxLabel(row?: TableRow): string {
    //     if (!row) {
    //         return `${this.isAllSelected() ? 'Deselect All' : 'Select All'}`;
    //     }
    //     return `${this.selection.isSelected(row) ? 'Remove from Set' : 'Add to SKU Set'}`;
    // }

    // public onSubmitFeedback($event: Event): void {
    //     TODO
    // }

    public ngOnDestroy(): void {
        this.resultsSubscription.unsubscribe();
    }
}
